// Confirm or correct one value read from the renewal dec page (spec C10 Corrections): the kit Dialog
// dialog with Prior (AMS360), the value read with View on dec page, the value to keep, and
// "This corrects the portal only". Markup from design/kit/components/Dialog plus the Record board's
// field dialog (fldgrid, anote calm, fld-err). View on dec page closes the dialog while the PDF is
// open and brings it back, with what was typed, when the viewer closes (as on the board).
import { useState } from 'react';
import type { Api, FetchView, Reading, ReadingFixField, RenewalDetail } from '../auth/api';
import { Dialog } from '../shell/Dialog';
import { InfoIcon } from '../shell/icons';
import { Viewer } from '../shell/Viewer';
import {
  fixLabels,
  fixLine,
  inputValue,
  keptForm,
  parseInput,
  priorOf,
  showValue,
  fixToast,
} from './fix-lines';
import { checkText } from './reading-lines';

type Props = {
  api: Api;
  renewal: RenewalDetail;
  reading: Reading;
  field: ReadingFixField;
  /** The renewal dec page's file name, for the viewer. */
  decName: string;
  onClose: () => void;
  /** `fixId` when the save can be undone from the toast. */
  onSaved: (view: FetchView, toast: string, fixId?: string) => void;
};

export function FixDialog({ api, renewal, reading, field, decName, onClose, onSaved }: Props) {
  const f = reading.fields.find((x) => x.field === field)!;
  const [typed, setTyped] = useState(() => inputValue(field, f.value));
  const [viewing, setViewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const subline = [
    renewal.insured,
    renewal.carrier ?? 'Carrier',
    renewal.line,
    renewal.policyNumber,
  ]
    .filter(Boolean)
    .join(' | ');

  const parsed = parseInput(field, typed);
  const error = parsed && 'error' in parsed ? parsed.error : null;
  const next = parsed && 'value' in parsed ? parsed.value : null;
  // The value read, kept as is: a confirm. Anything else: a correction.
  const action = next !== null && next === keptForm(field, f.read) ? 'confirm' : 'correct';
  const unchanged = next !== null && next === keptForm(field, f.value);
  const label = unchanged ? 'Confirm' : 'Save correction';

  async function save() {
    if (next === null) return;
    setBusy(true);
    setFailed(null);
    const res = await api.fixReading(renewal.id, {
      documentId: reading.documentId,
      field,
      action,
      ...(action === 'correct' ? { value: next } : {}),
    });
    setBusy(false);
    if (res.ok) {
      onSaved(res.body.fetch, fixToast(field, action), res.body.fixId);
      return;
    }
    const sent = res.body.fetch as FetchView | undefined;
    if (res.error === 'reading_changed' && sent) {
      onSaved(sent, 'The dec page was replaced. Check the new values.');
      return;
    }
    setFailed(
      res.error === 'bad_value'
        ? 'The portal could not take this value. Check it and try again.'
        : 'Could not save. Try again.',
    );
  }

  if (viewing) {
    return (
      <Viewer
        title={decName}
        subline={subline}
        src={api.documentUrl(reading.documentId)}
        downloadHref={api.documentUrl(reading.documentId, true)}
        kind="pdf"
        onClose={() => setViewing(false)}
      />
    );
  }

  const read = f.read === null ? 'Not read' : showValue(field, f.read);
  const line = fixLine(f);
  return (
    <Dialog
      id="fix-t"
      title={fixLabels[field]}
      subline={subline}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={next !== null ? 'btn btn-primary ml-auto' : 'btn ml-auto'}
            disabled={next === null || busy}
            onClick={() => void save()}
          >
            {label}
          </button>
        </>
      }
    >
      <dl className="fldgrid m0">
        <div>
          <dt>Prior, from AMS360</dt>
          <dd className="text2">{priorOf(field, renewal)}</dd>
        </div>
        <div>
          <dt>Read from dec page</dt>
          <dd className="row g8 wrap">
            <span className="ink">{read}</span>
            <button
              type="button"
              className="linkbtn"
              style={{ padding: 0, height: 'var(--space-20)' }}
              onClick={() => setViewing(true)}
            >
              View on dec page
            </button>
          </dd>
        </div>
      </dl>
      {f.check && <p className="m0 t12 amber">Check: {checkText({ field, reason: f.check })}.</p>}
      {line && <p className="m0 t12 muted">{line}</p>}
      <div>
        <label className="flab" htmlFor="fix-in">
          Value <span className="req">*</span>
        </label>
        <input
          id="fix-in"
          className={field === 'policyNumber' || field === 'totalPremium' ? 'input mono' : 'input'}
          value={typed}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? 'fix-err' : undefined}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && next !== null && !busy) void save();
          }}
        />
        {error && (
          <p id="fix-err" className="fld-err" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="anote calm">
        <InfoIcon className="ico s14" />
        <span>This corrects the portal only. AMS360 is not changed.</span>
      </div>
      {failed && (
        <p className="ferr m0" role="alert">
          {failed}
        </p>
      )}
    </Dialog>
  );
}
